/* eslint-disable react/no-array-index-key */
import React, { MouseEvent, KeyboardEvent, useRef, useEffect, ReactNode } from 'react'
import classNames from 'classnames'
import throttle from 'lodash/throttle'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage, InlineMessageProps } from 'transfix-ui/components/InlineMessage'
import { Box } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { CloseIcon } from 'transfix-icons'
import { useScrollLock } from 'utils/useScrollLock'
import { KEY_VALUES } from 'constants/keyValues'
import { ModalAction, IModalAction } from './ModalAction'

const OPEN_MODALS_SELECTOR = '.modal-container.active'
const HANDLE_KEYS = [KEY_VALUES.ENTER, KEY_VALUES.SPACE, KEY_VALUES.SPACEBAR].map(key =>
  key.toLowerCase()
)

export type ModalSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
export type TitleSize = 1 | 2 | 3 | 4

export enum MODAL_TYPES {
  form = 'modal',
  confirmation = 'modal confirmation',
}

export interface IModalBaseProps {
  modalType: MODAL_TYPES
  open?: boolean
  title?: React.ReactNode | string
  tag?: React.ReactNode | string | JSX.Element
  inlineMessageProps?: InlineMessageProps
  error?: string | string[]
  size?: ModalSize
  actions?: IModalAction[]
  dataTest?: string
  onClose: (action?: string) => any
  closeOnBackdropClick?: boolean
  bodyClassName?: string
  modalClassName?: string
  headerClassName?: string
  titleSize?: TitleSize
  titleClass?: string
  className?: string
  header?: JSX.Element | string
  footer?: JSX.Element
  additionalFooterInfo?: JSX.Element
  isClose?: boolean
  fullHeight?: boolean
  scrollableHeight?: boolean
  children?: React.ReactNode
  /**
   * @default false
   */
  fullWidth?: boolean
}

const handleResize = (
  { current: modal }: React.RefObject<HTMLDivElement>,
  { current: content }: React.RefObject<HTMLDivElement>
) => {
  if (content && modal) {
    const { clientHeight, scrollHeight, scrollTop } = content
    const overflow = scrollHeight > clientHeight
    let value = ''
    if (overflow) {
      const top = scrollTop > 0
      const bottom = scrollTop + clientHeight < scrollHeight
      if (top && bottom) {
        value = 'both'
      } else if (top) {
        value = 'top'
      } else if (bottom) {
        value = 'bottom'
      }
      modal.setAttribute('modal-overflow', value)
    } else {
      modal.removeAttribute('modal-overflow')
    }
  }
}

const createModalTitle = (
  title: React.ReactNode | string,
  titleSize: TitleSize,
  isMobile: boolean = false
): ReactNode => {
  switch (titleSize) {
    case 1:
      return <Typography variant='h1'>{title}</Typography>
    case 2:
      return <Typography variant='h2'>{title}</Typography>
    case 4:
      return <h4>{title}</h4>
    default:
      return <Typography variant={isMobile ? 'subtitle1' : 'h3'}>{title}</Typography>
  }
}

const useResize = (
  modalRef: React.RefObject<HTMLDivElement>,
  contentRef: React.RefObject<HTMLDivElement>
) => {
  useEffect(() => {
    const contentRefNode = contentRef.current
    const handler = throttle(() => {
      handleResize(modalRef, contentRef)
    }, 100)
    window.addEventListener('resize', handler)
    if (contentRefNode) {
      contentRefNode.addEventListener('scroll', handler)
    }
    return () => {
      window.removeEventListener('resize', handler)
      if (contentRefNode) {
        contentRefNode.removeEventListener('scroll', handler)
      }
    }
  }, [contentRef, modalRef])

  return () => handleResize(modalRef, contentRef)
}

export const ModalBase: React.FunctionComponent<IModalBaseProps> = ({
  modalType,
  open = false,
  title,
  titleSize = 3,
  titleClass,
  tag,
  inlineMessageProps,
  error,
  size = 4,
  children,
  actions = [],
  onClose,
  closeOnBackdropClick,
  dataTest = '',
  className = '',
  header,
  footer,
  additionalFooterInfo,
  isClose = false,
  fullHeight,
  scrollableHeight,
  bodyClassName = '',
  modalClassName: modalClassNameProp = '',
  headerClassName = '',
  fullWidth: fullWidthProp = false,
}) => {
  const { isMobile } = useBreakpointContext()
  const { cta, dataTest: messageDataTest, fullWidth, message, type } = inlineMessageProps || {}
  useScrollLock(open, OPEN_MODALS_SELECTOR, 'modal-open')
  const modalRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const resize = useResize(modalRef, bodyRef)

  // Scroll to top when opening any modal
  useEffect(() => {
    if (bodyRef?.current?.scrollTo && open) {
      bodyRef.current.scrollTo(0, 0)
    }
  }, [open])

  useEffect(() => {
    if (open) resize()
  }, [open, children, error, resize])

  const containerClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    const target = e.target as HTMLElement
    if (closeOnBackdropClick && target.classList?.contains('modal-container')) {
      onClose()
    }
  }

  const containerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (
      closeOnBackdropClick &&
      HANDLE_KEYS.includes(e.key.toLowerCase()) &&
      target.classList?.contains('modal-container')
    ) {
      onClose()
    }
  }

  const onClick = ({ currentTarget: { value } }: MouseEvent<HTMLButtonElement>) => {
    const { action } = actions.find(({ type: actionType }) => actionType === value) || {
      action: () => {},
    }
    if (action) {
      action()
    } else {
      onClose(value)
    }
  }

  const closeModal = () => {
    onClose()
  }

  const modalClassName = classNames(
    `${modalType}`,
    {
      [`modal-width-${size}`]: size,
      'full-height': fullHeight,
      'scrollable-height': scrollableHeight,
    },
    modalClassNameProp
  )

  const modalContainerClassName = classNames(
    `modal-container`,
    { [`active`]: open },
    `${className}`
  )

  const modalBodyClassName = classNames(`modal-body ${bodyClassName}`, {
    'flex-grow-1': fullHeight,
    /**
     * ph sass classes add padding with !important. The safest way to
     * overcome it is to remove or override them using another one.
     * In this case, overriding would not suffice as ph5 is below ph0, we have
     * to remove ph5 and add ph0 respectively using "fullWidth".
     */
    ph5: !(isMobile || fullWidthProp),
    ph0: fullWidthProp,
  })

  const modalTitle = createModalTitle(title, titleSize, isMobile)

  const titleClassName = titleClass || 'flex justify-between items-center'

  const additionalFooterClasses = classNames({
    'modal-footer flex flex-row justify-between': additionalFooterInfo,
  })

  const modalFooterClasses = classNames('pa5 tr', {
    'modal-footer': !additionalFooterInfo,
  })

  return (
    <div
      className={modalContainerClassName}
      role='button'
      tabIndex={0}
      onClick={containerClick}
      onKeyDown={containerKeyDown}
      data-test={dataTest}>
      <div className={modalClassName} ref={modalRef}>
        <div className={`modal-header pa5 ${headerClassName}`}>
          <div className={titleClassName}>
            <div className='flex flex-row items-center'>
              {title && modalTitle}
              {tag && <div className='ml3'>{tag}</div>}
            </div>
            <div className='flex items-center'>
              {header}
              {isClose && (
                <span>
                  <IconButton
                    Icon={CloseIcon}
                    onClick={closeModal}
                    label='Close Modal'
                    size='large'
                    data-test='close-modal'
                    color='tertiary'
                  />
                </span>
              )}
            </div>
          </div>
          {inlineMessageProps && (
            <div className='row mt2'>
              <div className='col-md-12 mb0'>
                <InlineMessage
                  message={message || ''}
                  type={type}
                  cta={cta}
                  dataTest={messageDataTest}
                  fullWidth={fullWidth}
                />
              </div>
            </div>
          )}
        </div>
        {children && (
          <div className={modalBodyClassName} ref={bodyRef}>
            {children}
          </div>
        )}
        {Boolean(footer) && footer}

        {Boolean(actions.length) && (
          <div className={additionalFooterClasses}>
            {additionalFooterInfo && (
              <div
                className='flex flex-row justify-start ml5 pv2'
                data-test='additional-footer-info'>
                {additionalFooterInfo}
              </div>
            )}
            <Box
              className={modalFooterClasses}
              display='flex'
              justifyContent='flex-end'
              alignItems='center'
              position='relative'>
              {actions &&
                actions.map(
                  (
                    {
                      BoxProps,
                      ButtonProps,
                      dataTest: actionDataTest,
                      disabled,
                      label,
                      loading,
                      type: actionType,
                    },
                    index
                  ) => (
                    <ModalAction
                      key={index}
                      label={label}
                      type={actionType}
                      disabled={disabled}
                      dataTest={actionDataTest}
                      onClick={onClick}
                      loading={loading}
                      ButtonProps={ButtonProps}
                      BoxProps={BoxProps}
                    />
                  )
                )}
            </Box>
          </div>
        )}
      </div>
    </div>
  )
}
